//TableListContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "../utils/axios";
import { useAuth } from "../context/AuthContext";

// Singleton class to manage table data
class TableDataManager {
  constructor() {
    this.tableEndpoints = [];
    this.loading = false;
    this.error = null;
    this.lastFetchTime = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
    this.listeners = new Set();
  }

  // Subscribe to data changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of data changes
  notify() {
    this.listeners.forEach(callback => callback({
      tableEndpoints: this.tableEndpoints,
      loading: this.loading,
      error: this.error
    }));
  }

  // Check if cache is still valid
  isCacheValid() {
    return this.lastFetchTime && 
           (Date.now() - this.lastFetchTime) < this.cacheTimeout &&
           this.tableEndpoints.length > 0 &&
           !this.error;
  }

  // Fetch table data if not cached or expired
  async fetchTableData(currentUser) {
    // If no user, clear data
    if (!currentUser) {
      this.tableEndpoints = [];
      this.loading = false;
      this.error = null;
      this.notify();
      return;
    }

    // Check if we have valid cached data
    if (this.isCacheValid()) {
      console.log("Using cached table data");
      this.notify();
      return;
    }

    // Set loading state
    this.loading = true;
    this.error = null;
    this.notify();

    try {
      
      
      const response = await axios.get("/api/tables/available");
      
      
      this.tableEndpoints = response.data || [];
      this.lastFetchTime = Date.now();
      this.error = null;
      
    } catch (err) {
      console.error("Failed to load tables:", err);
      console.error("Error details:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      
      this.error = err?.response?.data?.detail || err.message || "Failed to load tables";
      this.tableEndpoints = [];
    } finally {
      this.loading = false;
      this.notify();
    }
  }

  // Get current state
  getState() {
    return {
      tableEndpoints: this.tableEndpoints,
      loading: this.loading,
      error: this.error
    };
  }

  // Force refresh (ignore cache)
  async forceRefresh(currentUser) {
    this.lastFetchTime = null;
    await this.fetchTableData(currentUser);
  }

  // Clear cache
  clearCache() {
    this.tableEndpoints = [];
    this.lastFetchTime = null;
    this.error = null;
    this.notify();
  }
}

// Create singleton instance
const tableDataManager = new TableDataManager();

// Context
const TableListContext = createContext({
  tableEndpoints: [],
  loading: false,
  error: null,
  forceRefresh: () => {},
  clearCache: () => {}
});

export function TableListProvider({ children }) {
  const { currentUser } = useAuth();
  const [state, setState] = useState(tableDataManager.getState());

  useEffect(() => {
    // Subscribe to data changes
    const unsubscribe = tableDataManager.subscribe(setState);
    
    // Fetch data if needed
    tableDataManager.fetchTableData(currentUser);

    return unsubscribe;
  }, [currentUser]);

  const contextValue = {
    ...state,
    forceRefresh: () => tableDataManager.forceRefresh(currentUser),
    clearCache: () => tableDataManager.clearCache()
  };

  return (
    <TableListContext.Provider value={contextValue}>
      {children}
    </TableListContext.Provider>
  );
}

export function useTableList() {
  return useContext(TableListContext);
}

// Export the manager for direct use if needed
export { tableDataManager };

// Utility functions for direct data access
export const getTableEndpoints = () => tableDataManager.getState().tableEndpoints;
export const getTableByPath = (path) => {
  const endpoints = tableDataManager.getState().tableEndpoints;
  return endpoints.find(table => table.path === path);
};
export const getTablesByCategory = (category) => {
  const endpoints = tableDataManager.getState().tableEndpoints;
  return endpoints.filter(table => table.table_category === category);
};
export const getAllCategories = () => {
  const endpoints = tableDataManager.getState().tableEndpoints;
  const categories = new Set(endpoints.map(table => table.table_category));
  return Array.from(categories);
};